import './modal-task-view.scss';
import React from 'react';
import Modal from '@mui/material/Modal';
import {Box} from '@mui/material';
import {useTranslation} from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import {jwtDecode} from 'jwt-decode';
import dayjs from 'dayjs';
import Grid from '@mui/material/Grid2';
import {useNavigate} from 'react-router-dom';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 614,
  boxSizing: 'border-box',
  textAlign: 'left',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

function ModalTaskView({open, onClose, task, onEdit, onRemove, onCompletedChanged}) {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const token = sessionStorage.getItem('token') || null;
  const decodedToken = jwtDecode(token);
  const loggedUser = JSON.parse(sessionStorage.getItem('loggedUser') || 'null');
  const userArea = loggedUser?.area_code;
  const pmaId = decodedToken?.id;

  const [taskFull, setTaskFull] = React.useState(task || null);
  const [owners, setOwners] = React.useState([]);

  React.useEffect(() => {
    setTaskFull(task || null);
  }, [task]);

  React.useEffect(() => {
    if (!open || !token || !pmaId) return;

    let alive = true;

    (async () => {
      try {
        const config = {headers: {Authorization: `Bearer ${token}`}};
        const ownersRes = await apiClient.get(`${process.env.REACT_APP_API_URL}/pma/all`, config);

        if (!alive) return;
        setOwners(Array.isArray(ownersRes?.data) ? ownersRes.data : []);
      } catch (e) {
        if (!alive) return;
        setOwners([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, token, pmaId]);

  React.useEffect(() => {
    if (!open || !task?.id || !token) return;

    let alive = true;

    (async () => {
      try {
        const res = await apiClient.get(`${process.env.REACT_APP_API_URL}/calendar/event/${task.id}`, {
          headers: {Authorization: `Bearer ${token}`},
        });

        if (!alive) return;

        const details = res?.data ?? {};
        setTaskFull((prev) => ({
          ...(prev || {}),
          ...(details || {}),
          id: task.id,
        }));
      } catch (e) {
        if (!alive) return;
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, task?.id, token]);

  const isCompleted = React.useMemo(() => {
    const v = taskFull?.completed;
    return v === true || v === 1 || v === '1';
  }, [taskFull?.completed]);

  const dateLine = taskFull?.starting_date
    ? dayjs(taskFull.starting_date).locale(userArea?.toLowerCase() || 'en').format('dddd, D MMMM, YYYY')
    : '';

  const timeLine = taskFull?.starting_time ? dayjs(taskFull.starting_time, ['HH:mm:ss', 'HH:mm']).format('HH:mm') : '';

  const creatorLabel = React.useMemo(() => {
    const nameFromApi = String(taskFull?.created_by_name ?? '').trim();
    if (nameFromApi) return nameFromApi;

    const creatorId = taskFull?.created_by_id;
    if (!creatorId) return '';

    const found = owners.find((o) => String(o.id) === String(creatorId));
    if (!found) return String(creatorId);

    const name = String(found.name ?? '').trim();
    const last = String(found.last_name ?? '').trim();
    return `${name} ${last}`.trim() || String(creatorId);
  }, [taskFull?.created_by_name, taskFull?.created_by_id, owners]);

  const ownerLabel = React.useMemo(() => {
    const nameFromApi = String(taskFull?.owner_name ?? '').trim();
    if (nameFromApi) return nameFromApi;

    const ownerId = taskFull?.owner_id;
    if (!ownerId) return '';

    const found = owners.find((o) => String(o.id) === String(ownerId));
    if (!found) return String(ownerId);

    const name = String(found.name ?? '').trim();
    const last = String(found.last_name ?? '').trim();
    return `${name} ${last}`.trim() || String(ownerId);
  }, [taskFull?.owner_name, taskFull?.owner_id, owners]);

  const hacLabels = React.useMemo(() => {
    const assigned = Array.isArray(taskFull?.assigned_hacs) ? taskFull.assigned_hacs : [];
    return assigned
      .map((h) => {
        const id = h?.id;
        const name = String(h?.name ?? '').trim();
        const last = String(h?.last_name ?? '').trim();
        const label = `${name} ${last}`.trim() || String(h?.name ?? h?.id ?? '').trim();
        return {id, label};
      })
      .filter((x) => x.id);
  }, [taskFull?.assigned_hacs]);

  const canRemove = React.useMemo(() => {
    if (!pmaId) return false;
    const creatorId = taskFull?.created_by_id ?? null;
    if (!creatorId) return false;
    return String(pmaId) === String(creatorId);
  }, [pmaId, taskFull?.created_by_id]);

  const handleEditClick = (e) => {
    e?.stopPropagation?.();
    if (typeof onEdit === 'function') onEdit(taskFull);
    if (typeof onClose === 'function') onClose();
  };

  const handleRemoveClick = (e) => {
    e?.stopPropagation?.();
    if (typeof onRemove === 'function') onRemove(taskFull);
    if (typeof onClose === 'function') onClose();
  };

  const handleToggleCompleted = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!token) return;
    const id = taskFull?.id ?? task?.id ?? null;
    if (!id) return;

    try {
      await apiClient.patch(
        `${process.env.REACT_APP_API_URL}/calendar/event/${id}`,
        {completed: true},
        {headers: {Authorization: `Bearer ${token}`}}
      );

      setTaskFull((prev) => ({...(prev || {}), completed: true}));

      if (typeof onCompletedChanged === 'function') onCompletedChanged();
      if (typeof onClose === 'function') onClose();
    } catch (err) {
    }
  };

  const handleGoToHac = (e, hac) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!hac?.id) return;

    if (typeof onClose === 'function') onClose();

    navigate(`/common/personal-details/${hac.id}`, {
      state: {breadcrumbName: hac.label, origin: 'calendar'},
    });
  };

  return (
    <Modal open={open} onClose={onClose} className="modal-task-view">
      <Box sx={style} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <button className="close" type="button" onClick={onClose}>
          <span className="icon-close"/>
        </button>

        <h2>
          <span className={'cat-' + (taskFull?.category_id ?? taskFull?.category ?? 0)}></span>
          {taskFull?.title || ''}
        </h2>

        <div className="date">
          <div className="title">
            <span className="icon-clock"></span>
            {t('calendar-date')}
          </div>
          <div>
            <div>{dateLine}</div>
            {timeLine ? <div>{timeLine}</div> : null}
          </div>
        </div>

        <Grid container spacing={2} className="separator">
          <Grid size={{xs: 12, sm: 12, md: 6}}>
            <div className="creator">
              <div className="title">
                <span className="icon-user"></span>
                <span>{t('calendar-created-by')}</span>
              </div>
              <div>{creatorLabel ? <span className="chip">{creatorLabel}</span> : null}</div>
            </div>
          </Grid>

          <Grid size={{xs: 12, sm: 12, md: 6}}>
            <div className="owner">
              <div className="title">
                <span className="icon-user"></span>
                <span>{t('common-owner')}</span>
              </div>
              <div>{ownerLabel ? <span className="chip">{ownerLabel}</span> : null}</div>
            </div>
          </Grid>
        </Grid>


        <div className="hacs">
          <div className="title">
            <span className="icon-user"></span>
            <span>RRC members tagged</span>
          </div>
          <div>
            {hacLabels.length > 0 ? (
              hacLabels.map((h) => (
              <span
                className="chip clickable"
                key={String(h.id)}
                role="button"
                tabIndex={0}
                onClick={(e) => handleGoToHac(e, h)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleGoToHac(e, h);
                }}
              >
                {h.label}
                </span>
              ))) : (
                <p className="note">No RRC Members tagged</p>
            )}
          </div>
        </div>


        <div className="desc">
          <div className="title">
            <span className="icon-note"></span>
            <span>{t('common-description')}</span>
          </div>
          <div>{taskFull?.description || (<p className="note">No description added</p>)}</div>
        </div>

        <div className="actions">
          {canRemove ? (
            <button className="btn secondary" type="button" onClick={handleRemoveClick}>
              {t('common-remove')}
            </button>
          ) : null}

          {!isCompleted && (
            <button className="btn secondary" type="button" onClick={handleEditClick}>
              {t('common-edit')}
            </button>
          )}

          {!isCompleted && taskFull?.created_by_id !== 'SYSTEM' ? (
            <button className="btn primary" type="button" onClick={handleToggleCompleted}>
              {t('calendar-mark-completed')}
            </button>
          ) : null}
        </div>

        {isCompleted ? <p className="note txt-right">Completed by: {taskFull?.complete_by_name}</p> : null}
      </Box>
    </Modal>
  );
}

export default ModalTaskView;
